/**
 * SM-SAVE (§5.4): the editor's save state machine, with ALG-CONFLICT-STITCHY and ALG-APPLY-REMOTE
 * (§5.5). Pure: every side effect (the PUT, the GET-and-replace, timers, the dialog, toasts) is an
 * injected {@link SaveMachineEffects}, so each numbered transition is tested without Angular or
 * The engine (`save-machine.spec.ts`).
 *
 * One deliberate difference from the pack. §5.4 says a retry after a transition-11 mutation "sends
 * the latest local snapshot with a new X-Save-Id". If the first attempt did commit and only its
 * response was lost, that fresh save carries the old base, gets 409 with `lastActor = "user"`,
 * and opens the other-tab conflict dialog over the user's own save. So a retry always re-sends the
 * same body with the same save id, which the server answers from its receipt if it committed, and
 * the newer edits follow as an ordinary save once it returns (as transition 6 does for
 * `saving_dirty`).
 */
import { SHEETS_STRINGS } from '@sharedlib/sheets-editor/sheets.strings';

export type SaveMachineState =
  | 'clean'
  | 'dirty'
  | 'saving'
  | 'saving_dirty'
  | 'retry_wait'
  | 'conflict_user'
  | 'error_blocked'
  | 'read_only';

/** What a PUT came back as, already classified (§5.4's SAVE_* events). */
export type PutOutcome =
  | { readonly kind: 'ok'; readonly revision: number }
  | { readonly kind: 'conflict'; readonly actor: 'user' | 'stitchy'; readonly revision: number }
  /** A network failure, the 20 s timeout, or a 5xx; `afterMs`, the server's own `retryAfterMs` (ENGINE_BUSY). */
  | { readonly kind: 'retryable'; readonly afterMs?: number }
  /** 413 or 422 (and anything else that retrying cannot fix); `reason` is the server's message. */
  | { readonly kind: 'fatal'; readonly reason: string }
  | { readonly kind: 'trashed' };

export interface SaveMachineEffects {
  now(): number;
  /** The local document, as it is now. */
  snapshot(): unknown;
  put(snapshot: unknown, base: number, saveId: string): Promise<PutOutcome>;
  newSaveId(): string;
  setTimer(ms: number, fire: () => void): unknown;
  clearTimer(timer: unknown): void;
  /**
   * Loads the stored workbook into the engine (clearing undo), and returns its revision. With `apply`, it
   * replaces the grid only if `apply()` still holds once the stored workbook has arrived, and returns
   * null if it doesn't (the grid kept as it is).
   */
  reload(apply?: () => boolean): Promise<number | null>;
  setEditable(editable: boolean): void;
  openConflictDialog(): void;
  /** `viewRevision`: the revision "View versions" opens version history at (Stitchy's replaced change, SBUX-1146). */
  toast(text: string, viewRevision?: number): void;
  /**
   * ALG-APPLY-REMOTE step 4 for Stitchy's revision: the grid now shows `revision`. `previous` is
   * the document it replaced, which the toast's Undo restores (AM-38).
   */
  stitchyApplied(revision: number, previous: unknown): void;
  /** The state, or its banner reason, changed. */
  changed(): void;
  /**
   * After the editor has gone ({@link SaveMachine.leave}), the edits it left could not be saved: a conflict
   * with another tab, the Trash, or a save that kept failing. Nobody is left to ask.
   */
  leftUnsaved(reason: 'conflict' | 'trashed' | 'failed'): void;
  illegal(message: string): void;
}

/** §5.4 timers. */
export const DEBOUNCE_MS = 1_500;
export const MAXWAIT_MS = 10_000;
export const MAX_ATTEMPTS = 5;
export const retryDelay = (attempt: number): number => Math.min(1_000 * 2 ** (attempt - 1), 30_000);

/** §5.5 toasts, exact. */
export const STITCHY_REPLACED_TOAST = SHEETS_STRINGS["Your edits were kept. Stitchy's last change was replaced."];
export const OTHER_TAB_TOAST = SHEETS_STRINGS['Updated from another tab.'];
/** The banner reason after five failed attempts, which §5.4 does not word. */
export const UNREACHABLE_REASON = SHEETS_STRINGS["The server can't be reached."];

export class SaveMachine {
  private current: SaveMachineState = 'clean';
  private base: number;
  private dirtySince: number | null = null;
  private lastMutationAt: number | null = null;
  private attempt = 0;
  private inFlight: { snapshot: unknown; saveId: string } | null = null;
  /** Edits made while the in-flight save cannot include them (retry_wait after transition 11). */
  private newerLocal = false;
  private conflictRevision: number | null = null;
  /** Stitchy's revision that this tab's save replaced (transition 12), until the save's toast names it. */
  private replacedStitchy: number | null = null;
  private flushAfterSave = false;
  /** The editor has gone: its last document, which every later save sends ({@link leave}). */
  private left: { snapshot: unknown } | null = null;
  private reason: string | null = null;
  private debounceTimer: unknown = null;
  private maxwaitTimer: unknown = null;
  private retryTimer: unknown = null;
  private readonly reported = new Set<string>();
  /** {@link settled}'s callers, waiting for a state with nothing on its way to the server. */
  private settledWaiters: ((state: SaveMachineState) => void)[] = [];
  /** ITEM_RESTORED's reload is under way. */
  private restoring = false;
  /** The save that was in flight when the workbook went to Trash (`itemTrashed`). */
  private letGo: { snapshot: unknown; saveId: string } | null = null;

  /**
   * `trashed`: the workbook is in Trash as it opens. AC-LIFE-2: "the editor opens it read-only with
   * the Appendix F banner", so the machine starts where row 19 would take it, rather than learning of
   * the Trash from its first save's 423 (which let a person type into a workbook that could not keep it).
   */
  public constructor(
    private readonly effects: SaveMachineEffects,
    baseRevision: number,
    { trashed = false }: { trashed?: boolean } = {},
  ) {
    this.base = baseRevision;
    if (trashed) {
      this.current = 'read_only';
      this.effects.setEditable(false);
    }
  }

  public get state(): SaveMachineState {
    return this.current;
  }

  public get baseRevision(): number {
    return this.base;
  }

  /** The `error_blocked` banner's reason. */
  public get blockedReason(): string | null {
    return this.reason;
  }

  /** `beforeunload` asks the browser to confirm leaving in these states (§5.4, "Leaving the page"). */
  public get hasUnsavedWork(): boolean {
    // `conflict_user` too (§5.4 doesn't list it): the edits wait on the dialog, so leaving loses them, and
    // the session must say so, or Stitchy writes while the user decides.
    return ['dirty', 'saving', 'saving_dirty', 'retry_wait', 'error_blocked', 'conflict_user'].includes(this.current);
  }

  /**
   * Moving elsewhere in the app asks first in these states: the edits are ones {@link leave} can't save, a
   * blocked save or a conflict waiting on the dialog. In the others, `leave` saves them (an edit waiting on
   * the debounce, a save in flight or waiting to retry), so autosave's ordinary states don't ask.
   */
  public get hasUnsavableWork(): boolean {
    return this.current === 'error_blocked' || this.current === 'conflict_user';
  }

  // --------------------------------------------------------------------------------------- events

  /** LOCAL_MUTATION: transitions 1, 2, 4, 7, 11, 18. */
  public localMutation(): void {
    const now = this.effects.now();
    switch (this.current) {
      case 'clean':
        this.dirtySince = this.lastMutationAt = now;
        this.enterDirty();
        return;
      case 'dirty':
        this.lastMutationAt = now;
        this.armDebounce();
        return;
      case 'saving':
        this.lastMutationAt = now;
        return this.go('saving_dirty');
      case 'saving_dirty':
        this.lastMutationAt = now;
        return;
      case 'error_blocked':
        this.lastMutationAt = now;
        // A kept save is re-sent by "Try again"; these edits follow it.
        if (this.inFlight) this.newerLocal = true;
        return;
      case 'retry_wait':
        this.lastMutationAt = now;
        this.newerLocal = true;
        return;
      default:
        return this.reject('LOCAL_MUTATION');
    }
  }

  /** REMOTE_REVISION: transitions 21 and 22. */
  public async remoteRevision(actor: 'user' | 'stitchy', revision: number): Promise<void> {
    if (revision <= this.base) return;
    switch (this.current) {
      case 'clean': {
        // ALG-APPLY-REMOTE. Clean, so the local document is the base revision's: Undo's target.
        const previous = actor === 'stitchy' ? this.effects.snapshot() : undefined;
        // Only onto the document it would replace: an edit made while the stored workbook was fetched
        // keeps the grid, and its save meets the remote revision as a 409, as transition 22's does.
        const revision = await this.effects.reload(() => this.current === 'clean');
        if (revision === null || revision <= this.base) return;
        this.base = revision;
        if (actor === 'user') this.effects.toast(OTHER_TAB_TOAST);
        else this.effects.stitchyApplied(this.base, previous);
        return this.effects.changed();
      }
      case 'dirty':
      case 'saving':
      case 'saving_dirty':
      case 'retry_wait':
        // Transition 22 records `pendingRemote`, "resolved when the next save returns 409". Nothing
        // else reads it, and that 409 carries the revision and actor itself, so it is not kept:
        // the next save meets the remote revision either way. This tab's own save's event, which
        // can arrive before its response, lands here too and is rightly ignored.
        return;
      default:
        return this.reject('REMOTE_REVISION');
    }
  }

  /**
   * Leaving the page (visibility hidden, route change): save now rather than after the debounce.
   * A save already in flight is followed at once by the next one.
   */
  public flush(): void {
    if (this.current === 'dirty') this.startSave();
    else if (this.current === 'saving_dirty') this.flushAfterSave = true;
  }

  /** USER_RELOAD (transition 14): the other tab's version replaces this one. */
  public async userReload(): Promise<void> {
    if (this.current !== 'conflict_user') return this.reject('USER_RELOAD');
    this.base = (await this.effects.reload()) ?? this.base;
    this.dirtySince = null;
    this.newerLocal = false;
    this.go('clean');
  }

  /** USER_KEEP_MINE (transition 15): this tab's version is saved over the other. */
  public userKeepMine(): void {
    if (this.current !== 'conflict_user') return this.reject('USER_KEEP_MINE');
    this.base = this.conflictRevision ?? this.base;
    this.startSave();
  }

  /**
   * USER_RETRY (transition 17). A save that failed on the network is re-sent as it was, with its save id,
   * as the automatic retries are: if it did commit, the server answers from its receipt, rather than a new
   * save on the old base meeting the user's own revision as another tab's (409). Edits since follow it.
   */
  public userRetry(): void {
    if (this.current !== 'error_blocked') return this.reject('USER_RETRY');
    this.attempt = 0;
    this.reason = null;
    if (this.inFlight) return this.send(this.inFlight);
    this.startSave();
  }

  /**
   * ITEM_RESTORED (transition 20): out of Trash, editable again, from the stored version. The editor's own
   * "Restore" and the restore event (§12 #6: "open editors receive ITEM_RESTORED") can both arrive: the
   * second, while the first reloads, does nothing.
   */
  public async itemRestored(): Promise<void> {
    if (this.current !== 'read_only') return this.reject('ITEM_RESTORED');
    if (this.restoring) return;
    this.restoring = true;
    try {
      this.base = (await this.effects.reload()) ?? this.base;
    } finally {
      this.restoring = false;
    }
    this.effects.setEditable(true);
    this.dirtySince = null;
    this.go('clean');
  }

  /**
   * ITEM_TRASHED: SM-HOST's "ready → read_only (trashed)" (§11.1), when the workbook goes to Trash while it is
   * open (another tab, Stitchy, the item menu). Transition 19's action, without waiting for a save to meet
   * the Trash's 423: the grid turns read-only with the banner now, rather than taking edits that §12 refuses
   * (`ITEM_TRASHED`). Edits not yet saved can't be: the server refuses a trashed workbook's saves. A save in
   * flight is let go; its answer changes nothing (the restore reloads). Repeated, it does nothing (AM-57).
   */
  public itemTrashed(): void {
    if (this.current === 'read_only') return;
    this.letGo = this.inFlight;
    this.inFlight = null;
    this.newerLocal = false;
    this.flushAfterSave = false;
    this.clearAllTimers();
    this.effects.setEditable(false);
    this.go('read_only');
    if (this.left) this.effects.leftUnsaved('trashed');
  }

  // -------------------------------------------------------------------------------------- saving

  /** Transition 3 (and 15, 17): a new save of the local document as it is now. */
  private startSave(): void {
    this.clearDirtyTimers();
    this.newerLocal = false;
    this.send({ snapshot: this.left ? this.left.snapshot : this.effects.snapshot(), saveId: this.effects.newSaveId() });
  }

  private send(request: { snapshot: unknown; saveId: string }): void {
    this.inFlight = request;
    this.go(this.newerLocal ? 'saving_dirty' : 'saving');
    const base = this.base;
    void this.effects.put(request.snapshot, base, request.saveId).then(
      (outcome) => this.answered(request, outcome),
      () => this.answered(request, { kind: 'retryable' }),
    );
  }

  /** A save's answer; for the save let go when the workbook went to Trash (`itemTrashed`), nothing to do. */
  private answered(request: { snapshot: unknown; saveId: string }, outcome: PutOutcome): void {
    if (request === this.letGo) {
      this.letGo = null;
      return;
    }
    this.settle(outcome);
  }

  private settle(outcome: PutOutcome): void {
    if (this.current !== 'saving' && this.current !== 'saving_dirty') return this.reject(`SAVE_${outcome.kind.toUpperCase()}`);
    const hadNewer = this.current === 'saving_dirty';
    switch (outcome.kind) {
      case 'ok': {
        // Transitions 5 and 6.
        this.base = outcome.revision;
        this.attempt = 0;
        this.inFlight = null;
        if (this.replacedStitchy !== null) {
          const replaced = this.replacedStitchy;
          this.replacedStitchy = null;
          this.effects.toast(STITCHY_REPLACED_TOAST, replaced);
        }
        if (!hadNewer) {
          this.dirtySince = null;
          return this.go('clean');
        }
        this.dirtySince = this.effects.now();
        if (this.flushAfterSave || this.left) {
          this.flushAfterSave = false;
          return this.startSave();
        }
        return this.enterDirty();
      }
      case 'retryable':
        // Transitions 8 and 9.
        if (this.attempt < MAX_ATTEMPTS) {
          this.attempt += 1;
          this.newerLocal = hadNewer;
          this.go('retry_wait');
          // AM-100: after ENGINE_BUSY, SM-SAVE "retries after `retryAfterMs`" (5 s): sooner, the whole snapshot went
          // back into a budget that had just said it was full. Never sooner than the backoff.
          this.retryTimer = this.effects.setTimer(Math.max(retryDelay(this.attempt), outcome.afterMs ?? 0), () => this.retry());
          return;
        }
        this.reason = UNREACHABLE_REASON;
        this.go('error_blocked');
        if (this.left) this.effects.leftUnsaved('failed');
        return;
      case 'conflict':
        this.inFlight = null;
        if (outcome.actor === 'stitchy') {
          // Transition 12, ALG-CONFLICT-STITCHY: the user's work wins, re-sent on the new base.
          this.base = outcome.revision;
          this.replacedStitchy = outcome.revision;
          return this.startSave();
        }
        // Transition 13.
        this.conflictRevision = outcome.revision;
        this.go('conflict_user');
        return this.left ? this.effects.leftUnsaved('conflict') : this.effects.openConflictDialog();
      case 'fatal':
        // Transition 16.
        this.inFlight = null;
        this.reason = outcome.reason;
        this.go('error_blocked');
        if (this.left) this.effects.leftUnsaved('failed');
        return;
      case 'trashed':
        // Transition 19.
        this.inFlight = null;
        this.clearAllTimers();
        this.effects.setEditable(false);
        this.go('read_only');
        if (this.left) this.effects.leftUnsaved('trashed');
        return;
    }
  }

  /** RETRY_TIMER (transition 10): the same body, the same save id. */
  private retry(): void {
    this.retryTimer = null;
    if (this.current !== 'retry_wait' || !this.inFlight) return this.reject('RETRY_TIMER');
    this.send(this.inFlight);
  }

  // --------------------------------------------------------------------------------------- timers

  private enterDirty(): void {
    this.go('dirty');
    this.armDebounce();
    if (this.maxwaitTimer === null && this.dirtySince !== null) {
      const wait = Math.max(0, this.dirtySince + MAXWAIT_MS - this.effects.now());
      this.maxwaitTimer = this.effects.setTimer(wait, () => this.elapsed());
    }
  }

  private armDebounce(): void {
    if (this.debounceTimer !== null) this.effects.clearTimer(this.debounceTimer);
    const since = this.lastMutationAt ?? this.effects.now();
    const wait = Math.max(0, since + DEBOUNCE_MS - this.effects.now());
    this.debounceTimer = this.effects.setTimer(wait, () => this.elapsed());
  }

  /** DEBOUNCE_ELAPSED or MAXWAIT_ELAPSED (transition 3). */
  private elapsed(): void {
    if (this.current === 'dirty') this.startSave();
  }

  private clearDirtyTimers(): void {
    for (const timer of [this.debounceTimer, this.maxwaitTimer]) if (timer !== null) this.effects.clearTimer(timer);
    this.debounceTimer = this.maxwaitTimer = null;
  }

  private clearAllTimers(): void {
    this.clearDirtyTimers();
    if (this.retryTimer !== null) this.effects.clearTimer(this.retryTimer);
    this.retryTimer = null;
  }

  /** Stops every timer. */
  public dispose(): void {
    this.clearAllTimers();
  }

  /**
   * The editor is going away (§5.4, "Leaving the page"). `snapshot` is its document, taken while it still
   * has one: what isn't saved is saved from it now, and that save, its retries, a Stitchy conflict's re-send
   * and the save after one in flight all outlive the editor, sending it. Before, a save in flight was
   * followed by one that asked the gone editor for its document, sent nothing, and was refused; and a
   * failed save was never retried. What still can't be saved is reported ({@link SaveMachineEffects.leftUnsaved}).
   */
  public leave(snapshot: unknown): void {
    this.left = { snapshot };
    this.clearDirtyTimers();
    if (this.current === 'dirty') this.startSave();
    else if (this.current === 'saving_dirty') this.flushAfterSave = true;
    // retry_wait: the retry goes on, and edits made since it was sent follow it (`newerLocal`).
  }

  // ---------------------------------------------------------------------------------------- misc

  private go(state: SaveMachineState): void {
    if (state !== 'dirty') this.clearDirtyTimers();
    this.current = state;
    this.effects.changed();
    if (!SaveMachine.PENDING.includes(state) && this.settledWaiters.length) {
      const waiters = this.settledWaiters;
      this.settledWaiters = [];
      for (const settle of waiters) settle(state);
    }
  }

  /** The states with edits on their way to the server: waiting to go, going, or about to go again. */
  private static readonly PENDING: readonly SaveMachineState[] = ['dirty', 'saving', 'saving_dirty', 'retry_wait'];

  /** Edits are on their way to the server (or about to be): {@link settled} would wait. */
  public get pending(): boolean {
    return SaveMachine.PENDING.includes(this.current);
  }

  /**
   * Once nothing is on its way to the server: `clean` when the edits are saved; otherwise the state that stopped
   * them (`error_blocked` after the retries, `conflict_user`, `read_only`). At once when nothing was. With
   * {@link flush} first, this is "save now, and say how it went": a copy of the workbook made from what is stored
   * ("Duplicate", "Save as template") waits for it, so it copies what the editor shows.
   */
  public settled(): Promise<SaveMachineState> {
    if (!SaveMachine.PENDING.includes(this.current)) return Promise.resolve(this.current);
    return new Promise((resolve) => this.settledWaiters.push(resolve));
  }

  /** "Any event in a state not listed is illegal, is ignored, and is reported once per session." */
  private reject(event: string): void {
    const message = `sheets-save: illegal ${event} in ${this.current}`;
    if (this.reported.has(message)) return;
    this.reported.add(message);
    this.effects.illegal(message);
  }
}
